import {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  Ref,
} from "react";
import { OverlayTriggerHandle } from "rsuite/esm/internals/Picker";
import { Popover, Whisper } from "rsuite";

import SingleReferenceRecord from "./ReferencesComponents/SingleReferenceRecord";
import MultiReferenceRecord from "./ReferencesComponents/MultiReferenceRecord";
import SubitemDrawer from "../../../components/Drawers/Subitems/SubitemDrawer";
import ViewRecordDrawer from "../../Drawers/Records/ViewRecordDrawer";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import useRecordMetadata from "../../../hooks/useRecordMetadata";
import useRecordImage from "../../../hooks/useRecordImage";
import useView from "../../../api/api-hooks/views/useView";
import { Module } from "../../../types/TenantInterfaces";
import DisplayRecord from "../DisplayRecord";
import Reference from "./Reference";
const emptyFunc = () => {};

interface Props {
  tableView?: boolean;
  record?: any;
  field?: any;
  existingValue?: any;
  modifyTrigger: "click" | "doubleclick";
  mode: "r" | "rw";
  onUpdate?: (data: any) => void;
  onSelectedChange?: (data: any) => void;
  submitting?: boolean;
  error?: any;
  customDisplayComponent?: any;
  createMode?: boolean;
  oneTap?: boolean;
  pageID: string;
  reusableViewReferenceFields?: any;
  moduleAPIName?: string;
}

/**
 *
 * @param {boolean} tableView - Whether or not this record is inside of Table or not.
 * @param {object} record - The parent record for this reference record.
 * @param {object} field - The field schema of this Reference.
 * @param {object} existingValue - The value of of this Reference.
 * @param {string} modifyTrigger - String describing trigger to open the ReferenceRecord. ex: "click".
 * @param {string} mode - String describing mode ReferenceRecord is in. "r" or "rw". (read or readWrite)
 * @param {function} onUpdate - Callback to be called when this reference is updated.
 * @param {boolean} submitting - Whether or not the record is updating.
 * @param {error} error - The error from onUpdate if one occurs.
 * @param {Component} customDisplayComponent - For supplying a custom display component for this ReferenceRecord.
 * @param {boolean} createMode - Used for Tables that don't save to API directly (MultiSubitem Tables)
 */
const ReferenceRecord = (
  {
    tableView = false,
    record,
    field,
    existingValue,
    modifyTrigger = "click",
    mode,
    onUpdate,
    onSelectedChange,
    submitting,
    customDisplayComponent,
    createMode,
    oneTap = false,
    pageID,
    reusableViewReferenceFields,
  }: Props,
  ref: Ref<{ focus: () => void }>
) => {
  const whisperRef = useRef<OverlayTriggerHandle>(null);
  const [showFieldSettings, setShowFieldSettings] = useState(false);
  const [selected, setSelected] = useState<any>(null);
  const [showDetailView, setShowDetailView] = useState(false);
  let [viewRecord, setViewRecord] = useState<{
    recordModule: Module;
    recordID: string;
    isSubitem?: boolean;
    subitemID?: string;
    subitemSchema?: any;
  } | null>(null);
  const [keepOpen, setKeepOpen] = useState(false);
  const [hideReference, setHideReference] = useState(false);
  const { imageField, imageFieldPresentation } = useRecordImage({
    moduleAPIName: existingValue?._ModuleAPIName,
  });
  const { metadataField, metadataFieldType } = useRecordMetadata({
    moduleAPIName: existingValue?._ModuleAPIName,
  });
  const fieldAPIName = field?.SubitemParent
    ? `${field?.SubitemParent}.${field?.APIName}`
    : field?.APIName;
  const viewRecordModuleAPIName = viewRecord?.recordModule?.APIName;
  const viewID = viewRecordModuleAPIName
    ? reusableViewReferenceFields?.[fieldAPIName]?.[viewRecordModuleAPIName]
    : undefined;
  const disabled = viewRecordModuleAPIName
    ? !reusableViewReferenceFields?.[fieldAPIName]?.[viewRecordModuleAPIName]
    : true;

  const { view } = useView({
    pageID,
    viewID,
    disabled,
  });

  // Anytime selected changes, call onSelectedChange if exists (Used in custom cases typically)
  useEffect(() => {
    if (!onSelectedChange) return;

    callFunctionIfExists(onSelectedChange, selected);
  }, [selected]);

  useEffect(() => {
    setSelected(existingValue);
  }, [existingValue]);

  const type = field?.FieldType;
  const isShareView = window.location.pathname.startsWith("/share/");
  const isSharedModule = ["Pages", "Views"].includes(
    existingValue?._ModuleAPIName ?? ""
  );

  useImperativeHandle(ref, () => ({
    focus: () => {
      whisperRef.current?.open();
    },
  }));

  return (
    <>
      <Whisper
        preventOverflow
        ref={whisperRef}
        placement="autoVertical"
        trigger={
          keepOpen || showFieldSettings || mode === "r" ? "none" : "click"
        }
        onClose={
          type === "SingleReference" ? saveSingleReference : saveMultiReference
        }
        speaker={
          <Popover
            full
            className={hideReference ? "d-none" : "p-0 sidebar-popover"}
          >
            <Reference
              field={field}
              onClose={stopEditing}
              selected={selected}
              setSelected={setSelected}
              setKeepOpen={setKeepOpen}
              setHideReference={setHideReference}
              defaultOpen={!existingValue}
              oneTap={oneTap}
              reusableViewReferenceFields={reusableViewReferenceFields}
              pageID={pageID}
              searchCustomField={
                (Array.isArray(field.SourcesList) &&
                  field.SourcesList[0].split(".").pop() === "People") ||
                (typeof field.SourcesList === "string" &&
                  field.SourcesList.split(".").pop() === "People")
                  ? "Name"
                  : undefined
              }
              showFieldSettings={showFieldSettings}
              setShowFieldSettings={setShowFieldSettings}
            />
          </Popover>
        }
      >
        <div>
          {customDisplayComponent ? (
            <div onClick={mode !== "r" ? startEditing : undefined}>
              {customDisplayComponent}
            </div>
          ) : (
            <DisplayRecord
              mode={mode}
              tableView={tableView}
              modifyTrigger={modifyTrigger}
              openModify={startEditing}
              isSensitive={field?.Sensitive ?? false}
              displayComponent={(_, innerRef) => {
                if (type === "SingleReference") {
                  return (
                    <SingleReferenceRecord
                      ref={innerRef}
                      onOpen={() => setShowDetailView(true)}
                      onView={
                        isShareView || isSharedModule
                          ? emptyFunc
                          : existingValue?._isSubitem
                          ? setViewRecord
                          : (mod: any) => {
                              setViewRecord({
                                recordModule: mod,
                                recordID: existingValue.id,
                              });
                            }
                      }
                      existingValue={existingValue}
                      submitting={submitting}
                      imageField={imageField || undefined}
                      imageFieldPresentation={imageFieldPresentation}
                      metadataField={metadataField}
                      metadataFieldType={metadataFieldType}
                      tableView={tableView}
                    />
                  );
                }

                if (type === "MultiReference") {
                  return (
                    <MultiReferenceRecord
                      ref={innerRef}
                      onOpen={() => setShowDetailView(true)}
                      onView={isShareView ? emptyFunc : setViewRecord}
                      existingValue={existingValue}
                      submitting={submitting}
                      tableView={tableView}
                      metadataField={metadataField}
                      metadataFieldType={metadataFieldType}
                    />
                  );
                }

                return <></>;
              }}
            />
          )}
        </div>
      </Whisper>
      {showDetailView && (
        <ViewRecordDrawer
          open={!!viewRecord && !viewRecord?.isSubitem}
          module={viewRecord?.recordModule as Module}
          recordID={viewRecord?.recordID}
          onClose={() => setViewRecord(null)}
          view={view}
          fromReusableViewReference={!!view}
        />
      )}
      {showDetailView && (
        <SubitemDrawer
          open={!!viewRecord && viewRecord?.isSubitem}
          recordID={viewRecord?.recordID}
          subitemID={viewRecord?.subitemID}
          moduleAPIName={viewRecord?.recordModule?.APIName}
          field={viewRecord?.subitemSchema}
          onClose={() => setViewRecord(null)}
          onExited={() => setShowDetailView(false)}
          displayMode={"SummaryCard"}
          reusableViewReferenceFields={reusableViewReferenceFields}
        />
      )}
    </>
  );

  function saveSingleReference() {
    if (!existingValue && !selected) return;
    if (existingValue?.id === selected?.id) return;
    if (!onUpdate) return;

    let payload;

    if (selected?._isSubitem) {
      payload = selected || null;
    } else {
      payload = selected
        ? {
            id: selected.id,
            _ModuleAPIName: selected._ModuleAPIName,
            _RecordName: selected._RecordName,
            Name: selected?.Name,
          }
        : null;
    }

    setSelected(null);

    onUpdate({
      field,
      recordID: record?._id || record?.id,
      payload: {
        [field?.APIName]: payload,
      },
    });
  }

  function saveMultiReference() {
    if (!onUpdate) return;

    const payload = [];

    let existing = existingValue?.length ? existingValue : [];
    let selected2 = selected?.length ? selected : [];

    if (createMode) return onUpdate({ payload: selected2 });

    // Calculate all the added records
    for (const reference of selected2) {
      if (
        existing &&
        !!existing.find((r: any) => {
          if (r?._isSubitem) {
            return r?._SubitemID === reference?._SubitemID;
          }

          return r.id === reference.id;
        })
      ) {
        continue;
      }

      payload.push({
        action: "Add",
        payload: reference?._isSubitem
          ? reference
          : {
              id: reference.id,
              _ModuleAPIName: reference._ModuleAPIName,
              _RecordName: reference._RecordName,
              Name: reference?.Name,
            },
      });
    }

    // Calculate all the removed records
    for (const reference of existing) {
      if (
        selected2 &&
        !!selected2.find((r: any) => {
          if (r?._isSubitem) {
            return r?._SubitemID === reference?._SubitemID;
          }

          return r.id === reference.id;
        })
      ) {
        continue;
      }

      payload.push({
        action: "Remove",
        payload: reference?._isSubitem
          ? reference
          : {
              id: reference.id,
              _ModuleAPIName: reference._ModuleAPIName,
              _RecordName: reference._RecordName,
            },
      });
    }

    if (!payload.length) return;

    onUpdate({
      fieldAPIName: field?.APIName,
      recordID: record?.id ?? record?._id,
      payload: payload,
      subitemParent: field?.SubitemParent,
    });
  }

  function startEditing() {
    if (!whisperRef.current) return;
    whisperRef.current.open();
  }

  function stopEditing() {
    if (!whisperRef.current) return;
    whisperRef.current.close();
  }
};

export default forwardRef(ReferenceRecord);
